import { useEffect, useState, useCallback } from 'react';
import { apiFetch } from '../../lib/api';
import { printReport } from '../../lib/printReport';

type ReportRange = 'month' | 'week' | '6months' | 'annual' | 'custom' | 'all';

interface CaseReport {
  id: number;
  client_name: string | null;
  file_reference: string | null;
  case_type: string | null;
  team_name: string | null;
  current_stage: string | null;
  file_status: string | null;
  file_open_date: string | null;
  file_close_date: string | null;
  total_case_value: number;
  amount_collected: number;
  // VAT pulled from the cases table. `vat_applicable` is stored as 0/1
  // (legacy SQLite-flavoured boolean); `vat_amount` is the £ figure.
  vat_applicable?: number;
  vat_amount?: number;
}

interface TeamPerf { name: string; target: number; achieved: number; ratio: number; }
interface DashSummary {
  total_revenue: number; total_collected: number; outstanding: number;
  total_cases: number; active_cases: number; closed_cases: number;
  team_performance: TeamPerf[];
}

function formatCurrency(v: number) {
  return '£' + v.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
function formatDate(d: string | null) { return d ? new Date(d).toLocaleDateString('en-GB') : '-'; }

function progressColor(pct: number) {
  if (pct >= 100) return 'var(--success)';
  if (pct >= 50) return 'var(--warning)';
  return 'var(--danger)';
}

function statusBadge(s: string | null) {
  if (!s) return 'badge badge-gray';
  const sl = s.toLowerCase();
  if (sl === 'open' || sl === 'in-progress') return 'badge badge-blue';
  if (sl === 'closed' || sl === 'settled') return 'badge badge-green';
  if (sl === 'dropped') return 'badge badge-red';
  return 'badge badge-gray';
}

const RANGE_OPTIONS: { value: ReportRange; label: string }[] = [
  { value: 'month', label: 'This Month' },
  { value: 'week', label: 'This Week' },
  { value: '6months', label: 'Last 6 Months' },
  { value: 'annual', label: 'Year to Date' },
  { value: 'all', label: 'All Time' },
  { value: 'custom', label: 'Custom' },
];

function getRangeDates(range: ReportRange, customFrom?: string, customTo?: string): { from: string; to: string } {
  const now = new Date();
  const todayStr = now.toISOString().split('T')[0];

  if (range === 'custom' && customFrom && customTo) return { from: customFrom, to: customTo };
  if (range === 'week') {
    const d = new Date(now);
    d.setDate(d.getDate() - 6);
    return { from: d.toISOString().split('T')[0], to: todayStr };
  }
  if (range === 'month') return { from: `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`, to: todayStr };
  if (range === '6months') {
    const d = new Date(now); d.setMonth(d.getMonth() - 5); d.setDate(1);
    return { from: d.toISOString().split('T')[0], to: todayStr };
  }
  if (range === 'annual') return { from: `${now.getFullYear()}-01-01`, to: todayStr };
  return { from: '2000-01-01', to: '2099-12-31' };
}

export default function ReportsPage() {
  const [caseReports, setCaseReports] = useState<CaseReport[]>([]);
  const [summary, setSummary] = useState<DashSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState<ReportRange>('month');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [activeCustom, setActiveCustom] = useState<{ from: string; to: string } | null>(null);
  const [sortBy, setSortBy] = useState<'client' | 'team' | 'stage' | 'fee' | 'collected'>('client');
  const [filterTeam, setFilterTeam] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [activeTab, setActiveTab] = useState<'cases' | 'performance' | 'closed'>('cases');

  const load = useCallback((r: ReportRange, from?: string, to?: string) => {
    setLoading(true);
    const { from: f, to: t } = from && to ? { from, to } : getRangeDates(r);
    const dashParams = new URLSearchParams({ range: r });
    if (r === 'custom' && from && to) { dashParams.set('from', from); dashParams.set('to', to); }

    const caseParams = new URLSearchParams({ from: f, to: t });

    Promise.all([
      apiFetch(`/api/reports/cases?${caseParams}`).then(res => res.json()),
      apiFetch(`/api/dashboard?${dashParams}`).then(res => res.json()),
    ]).then(([cases, dash]) => {
      setCaseReports(Array.isArray(cases) ? cases : []);
      setSummary(dash);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (range !== 'custom') load(range);
  }, [range, load]);

  const applyCustom = () => {
    if (customFrom && customTo) {
      setActiveCustom({ from: customFrom, to: customTo });
      load('custom', customFrom, customTo);
    }
  };

  // All unique teams from cases for filter dropdown
  const allTeams = Array.from(new Set(caseReports.map(c => c.team_name).filter(Boolean) as string[])).sort();

  let filtered = caseReports.filter(c => {
    if (filterTeam && c.team_name !== filterTeam) return false;
    if (filterStatus && c.file_status !== filterStatus) return false;
    return true;
  });

  filtered = [...filtered].sort((a, b) => {
    if (sortBy === 'client') return (a.client_name || '').localeCompare(b.client_name || '');
    if (sortBy === 'team') return (a.team_name || '').localeCompare(b.team_name || '');
    if (sortBy === 'stage') return (a.current_stage || '').localeCompare(b.current_stage || '');
    if (sortBy === 'fee') return b.total_case_value - a.total_case_value;
    if (sortBy === 'collected') return b.amount_collected - a.amount_collected;
    return 0;
  });

  const closedCases = filtered.filter(c => c.file_status?.toLowerCase() === 'closed' || c.file_close_date);

  const totalFee = filtered.reduce((s, c) => s + c.total_case_value, 0);
  const totalCollected = filtered.reduce((s, c) => s + c.amount_collected, 0);
  const totalVat = filtered.reduce((s, c) => s + (c.vat_amount ?? 0), 0);

  const collectionRate = summary?.total_revenue
    ? ((summary.total_collected / summary.total_revenue) * 100).toFixed(1) : '0.0';

  const rangeLabel =
    range === 'month' ? 'This Month' :
    range === 'week' ? 'This Week' :
    range === '6months' ? 'Last 6 Months' :
    range === 'annual' ? 'Year to Date' :
    range === 'all' ? 'All Time' :
    (activeCustom ? `${formatDate(activeCustom.from)} – ${formatDate(activeCustom.to)}` : 'Custom');

  function exportClientReport() {
    printReport({
      title: 'Client Report',
      subtitle: 'SKR Law — Case Status Summary',
      rangeLabel,
      summary: [
        { label: 'Cases', value: String(filtered.length) },
        { label: 'Total Fee', value: formatCurrency(totalFee) },
        { label: 'VAT', value: formatCurrency(totalVat) },
        { label: 'Collected', value: formatCurrency(totalCollected) },
        { label: 'Outstanding', value: formatCurrency(totalFee - totalCollected) },
      ],
      columns: [
        { label: 'Client', render: (r: CaseReport) => r.client_name || '—' },
        { label: 'File Ref', render: (r: CaseReport) => r.file_reference || '—' },
        { label: 'Type', render: (r: CaseReport) => r.case_type || '—' },
        { label: 'Team', render: (r: CaseReport) => r.team_name || '—' },
        { label: 'Stage', render: (r: CaseReport) => r.current_stage || '—' },
        { label: 'Status', render: (r: CaseReport) => r.file_status || '—' },
        { label: 'Opened', render: (r: CaseReport) => formatDate(r.file_open_date) },
        { label: 'Fee', align: 'right', render: (r: CaseReport) => formatCurrency(r.total_case_value) },
        { label: 'VAT', align: 'right', render: (r: CaseReport) => formatCurrency(r.vat_amount ?? 0) },
        { label: 'Collected', align: 'right', render: (r: CaseReport) => formatCurrency(r.amount_collected) },
        { label: 'Outstanding', align: 'right', render: (r: CaseReport) => formatCurrency(r.total_case_value - r.amount_collected) },
      ],
      rows: filtered,
    });
  }

  function exportClosedFiles() {
    printReport({
      title: 'Closed Files Report',
      subtitle: 'SKR Law',
      rangeLabel,
      summary: [
        { label: 'Closed Files', value: String(closedCases.length) },
        { label: 'Total Fee', value: formatCurrency(closedCases.reduce((s, c) => s + c.total_case_value, 0)) },
        { label: 'VAT', value: formatCurrency(closedCases.reduce((s, c) => s + (c.vat_amount ?? 0), 0)) },
        { label: 'Collected', value: formatCurrency(closedCases.reduce((s, c) => s + c.amount_collected, 0)) },
      ],
      columns: [
        { label: 'Client', render: (r: CaseReport) => r.client_name || '—' },
        { label: 'File Ref', render: (r: CaseReport) => r.file_reference || '—' },
        { label: 'Type', render: (r: CaseReport) => r.case_type || '—' },
        { label: 'Team', render: (r: CaseReport) => r.team_name || '—' },
        { label: 'Date Closed', render: (r: CaseReport) => formatDate(r.file_close_date) },
        { label: 'Fee', align: 'right', render: (r: CaseReport) => formatCurrency(r.total_case_value) },
        { label: 'VAT', align: 'right', render: (r: CaseReport) => formatCurrency(r.vat_amount ?? 0) },
        { label: 'Collected', align: 'right', render: (r: CaseReport) => formatCurrency(r.amount_collected) },
        { label: 'Outstanding', align: 'right', render: (r: CaseReport) => formatCurrency(r.total_case_value - r.amount_collected) },
      ],
      rows: closedCases,
    });
  }

  function exportTeamPerformance() {
    if (!summary) return;
    printReport({
      title: 'Team Performance Report',
      subtitle: 'SKR Law',
      rangeLabel,
      summary: [
        { label: 'Revenue', value: formatCurrency(summary.total_revenue) },
        { label: 'Collected', value: formatCurrency(summary.total_collected) },
        { label: 'Collection Rate', value: `${collectionRate}%` },
      ],
      columns: [
        { label: 'Team', render: (t: TeamPerf) => t.name },
        { label: 'Target', align: 'right', render: (t: TeamPerf) => formatCurrency(t.target) },
        { label: 'Achieved', align: 'right', render: (t: TeamPerf) => formatCurrency(t.achieved) },
        { label: 'Ratio', align: 'right', render: (t: TeamPerf) => `${(t.ratio || 0).toFixed(1)}%` },
      ],
      rows: summary.team_performance || [],
    });
  }

  if (!summary && loading) return <div style={{ padding: 40, textAlign: 'center' }}>Loading...</div>;

  return (
    <>
      <div className="page-header">
        <div>
          <h1 className="page-title">Reports</h1>
          <p className="page-subtitle">Financial and performance analytics</p>
        </div>
        <div style={{ display: 'flex', gap: 4, background: 'var(--bg-light)', borderRadius: 8, padding: 4 }}>
          {RANGE_OPTIONS.map(opt => (
            <button
              key={opt.value}
              onClick={() => setRange(opt.value)}
              style={{
                padding: '6px 12px', borderRadius: 6, border: 'none', cursor: 'pointer', fontSize: 12,
                fontWeight: range === opt.value ? 600 : 400,
                background: range === opt.value ? '#fff' : 'transparent',
                color: range === opt.value ? 'var(--primary)' : 'var(--text-secondary)',
                boxShadow: range === opt.value ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
              }}
            >{opt.label}</button>
          ))}
        </div>
      </div>

      {/* Custom date */}
      {range === 'custom' && (
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', marginBottom: 20, background: 'var(--bg-light)', borderRadius: 8, padding: '12px 16px' }}>
          <div><label className="form-label">From</label><input className="form-input" type="date" value={customFrom} onChange={e => setCustomFrom(e.target.value)} style={{ width: 160 }} /></div>
          <div><label className="form-label">To</label><input className="form-input" type="date" value={customTo} onChange={e => setCustomTo(e.target.value)} style={{ width: 160 }} /></div>
          <button className="btn btn-primary" onClick={applyCustom} disabled={!customFrom || !customTo}>Apply</button>
        </div>
      )}

      {loading && <div style={{ textAlign: 'center', padding: '8px 0', color: 'var(--text-muted)', fontSize: 13 }}>Loading...</div>}

      {/* Summary stats */}
      {summary && (
        <div className="stats-grid" style={{ marginBottom: 24 }}>
          {[
            { label: 'Total Revenue', value: formatCurrency(summary.total_revenue) },
            { label: 'Total Collected', value: formatCurrency(summary.total_collected) },
            { label: 'Outstanding', value: formatCurrency(summary.outstanding), danger: true },
            { label: 'Collection Rate', value: `${collectionRate}%` },
            { label: 'Total Cases', value: String(summary.total_cases) },
            { label: 'Closed Cases', value: String(summary.closed_cases ?? 0) },
          ].map(s => (
            <div key={s.label} className="stat-card">
              <div className="stat-label">{s.label}</div>
              <div className="stat-value" style={s.danger ? { color: 'var(--danger)' } : undefined}>{s.value}</div>
            </div>
          ))}
        </div>
      )}

      {/* Tabs */}
      <div className="tabs" style={{ marginBottom: 20 }}>
        <button className={`tab${activeTab === 'cases' ? ' active' : ''}`} onClick={() => setActiveTab('cases')}>Client Report</button>
        <button className={`tab${activeTab === 'performance' ? ' active' : ''}`} onClick={() => setActiveTab('performance')}>Team Performance</button>
        <button className={`tab${activeTab === 'closed' ? ' active' : ''}`} onClick={() => setActiveTab('closed')}>Closed Files ({closedCases.length})</button>
      </div>

      {activeTab === 'cases' && (
        <div className="card">
          <div className="card-header" style={{ flexWrap: 'wrap', gap: 12 }}>
            <h2 className="card-title">Client Report — {range === 'month' ? 'This Month' : range === 'week' ? 'This Week' : range === '6months' ? 'Last 6 Months' : range === 'annual' ? 'Year to Date' : range === 'custom' && activeCustom ? `${formatDate(activeCustom.from)} – ${formatDate(activeCustom.to)}` : 'All Time'}</h2>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <select className="form-input" value={filterTeam} onChange={e => setFilterTeam(e.target.value)} style={{ fontSize: 12 }}>
                <option value="">All Teams</option>
                {allTeams.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
              <select className="form-input" value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={{ fontSize: 12 }}>
                <option value="">All Statuses</option>
                {['Open', 'Closed', 'In-progress', 'In Court', 'Waiting on Client', 'Appealed', 'Dropped', 'Settled'].map(s => <option key={s} value={s}>{s}</option>)}
              </select>
              <select className="form-input" value={sortBy} onChange={e => setSortBy(e.target.value as typeof sortBy)} style={{ fontSize: 12 }}>
                <option value="client">Sort: Client</option>
                <option value="team">Sort: Team</option>
                <option value="stage">Sort: Stage</option>
                <option value="fee">Sort: Fee ↓</option>
                <option value="collected">Sort: Collected ↓</option>
              </select>
              <button className="btn btn-primary btn-sm" onClick={exportClientReport} disabled={filtered.length === 0}>Export PDF</button>
            </div>
          </div>

          {/* Totals row */}
          <div style={{ display: 'flex', gap: 24, padding: '10px 16px', background: 'var(--bg-light)', fontSize: 13, borderBottom: '1px solid var(--border)', flexWrap: 'wrap' }}>
            <span><strong>{filtered.length}</strong> cases</span>
            <span>Total Fee: <strong>{formatCurrency(totalFee)}</strong></span>
            <span>VAT: <strong>{formatCurrency(totalVat)}</strong></span>
            <span>Collected: <strong style={{ color: 'var(--success)' }}>{formatCurrency(totalCollected)}</strong></span>
            <span>Outstanding: <strong style={{ color: 'var(--danger)' }}>{formatCurrency(totalFee - totalCollected)}</strong></span>
          </div>

          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Client Name</th>
                  <th>File Ref</th>
                  <th>Case Type</th>
                  <th>Team</th>
                  <th>Current Stage</th>
                  <th>Status</th>
                  <th>File Opened</th>
                  <th>Fee</th>
                  <th>VAT</th>
                  <th>Collected</th>
                  <th>Outstanding</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 && <tr><td colSpan={11} style={{ textAlign: 'center', padding: 32, color: 'var(--text-secondary)' }}>No cases found for selected period</td></tr>}
                {filtered.map(c => {
                  const outstanding = c.total_case_value - c.amount_collected;
                  const vat = c.vat_amount ?? 0;
                  return (
                    <tr key={c.id}>
                      <td style={{ fontWeight: 600 }}>{c.client_name || '-'}</td>
                      <td>{c.file_reference || '-'}</td>
                      <td>{c.case_type || '-'}</td>
                      <td>{c.team_name || '-'}</td>
                      <td>
                        {c.current_stage
                          ? <span style={{ fontSize: 12, background: 'var(--bg-light)', padding: '2px 8px', borderRadius: 4, border: '1px solid var(--border)' }}>{c.current_stage}</span>
                          : <span style={{ color: 'var(--text-muted)' }}>—</span>}
                      </td>
                      <td><span className={statusBadge(c.file_status)}>{c.file_status}</span></td>
                      <td style={{ fontSize: 12 }}>{formatDate(c.file_open_date)}</td>
                      <td>{formatCurrency(c.total_case_value)}</td>
                      <td style={vat > 0 ? undefined : { color: 'var(--text-muted)' }}>
                        {vat > 0 ? formatCurrency(vat) : '—'}
                      </td>
                      <td style={{ color: 'var(--success)', fontWeight: 500 }}>{formatCurrency(c.amount_collected)}</td>
                      <td style={{ color: outstanding > 0 ? 'var(--danger)' : 'var(--success)', fontWeight: 500 }}>{formatCurrency(outstanding)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === 'performance' && summary && (
        <div className="card">
          <div className="card-header">
            <h2 className="card-title">Team Performance</h2>
            <button className="btn btn-primary btn-sm" onClick={exportTeamPerformance} disabled={!(summary.team_performance || []).length}>Export PDF</button>
          </div>
          <div className="table-container">
            <table>
              <thead>
                <tr><th>Team</th><th>Target</th><th>Achieved</th><th>Performance %</th></tr>
              </thead>
              <tbody>
                {(summary.team_performance || []).length === 0 && <tr><td colSpan={4} style={{ textAlign: 'center', padding: 20 }}>No team data</td></tr>}
                {(summary.team_performance || []).map(t => (
                  <tr key={t.name}>
                    <td>{t.name}</td>
                    <td>{formatCurrency(t.target)}</td>
                    <td>{formatCurrency(t.achieved)}</td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div className="progress-bar" style={{ flex: 1 }}>
                          <div className="progress-fill" style={{
                            width: `${Math.min(t.ratio || 0, 100)}%`,
                            background: progressColor(t.ratio || 0),
                          }} />
                        </div>
                        <span style={{ fontSize: 13, fontWeight: 600, minWidth: 40, color: progressColor(t.ratio || 0) }}>
                          {Math.min(t.ratio || 0, 100).toFixed(1)}%
                        </span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === 'closed' && (
        <div className="card">
          <div className="card-header">
            <h2 className="card-title">Closed Files</h2>
            <button className="btn btn-primary btn-sm" onClick={exportClosedFiles} disabled={closedCases.length === 0}>Export PDF</button>
          </div>
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Client Name</th>
                  <th>File Ref</th>
                  <th>Case Type</th>
                  <th>Team</th>
                  <th>Date Closed</th>
                  <th>Fee</th>
                  <th>VAT</th>
                  <th>Collected</th>
                  <th>Outstanding</th>
                </tr>
              </thead>
              <tbody>
                {closedCases.length === 0 && <tr><td colSpan={9} style={{ textAlign: 'center', padding: 32, color: 'var(--text-secondary)' }}>No closed files in this period</td></tr>}
                {closedCases.map(c => {
                  const outstanding = c.total_case_value - c.amount_collected;
                  const vat = c.vat_amount ?? 0;
                  return (
                    <tr key={c.id}>
                      <td style={{ fontWeight: 600 }}>{c.client_name || '-'}</td>
                      <td>{c.file_reference || '-'}</td>
                      <td>{c.case_type || '-'}</td>
                      <td>{c.team_name || '-'}</td>
                      <td style={{ fontSize: 12 }}>{formatDate(c.file_close_date)}</td>
                      <td>{formatCurrency(c.total_case_value)}</td>
                      <td style={vat > 0 ? undefined : { color: 'var(--text-muted)' }}>
                        {vat > 0 ? formatCurrency(vat) : '—'}
                      </td>
                      <td style={{ color: 'var(--success)', fontWeight: 500 }}>{formatCurrency(c.amount_collected)}</td>
                      <td style={{ color: outstanding > 0 ? 'var(--danger)' : 'var(--success)', fontWeight: 500 }}>{formatCurrency(outstanding)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  );
}
